/**
 * Fill between (W2-11 batch-tools; DESIGN.md §5.8, RPE Curve notes CURVE-01..04, E§4.11).
 *
 * Two notes A (earlier) and B on one line. New notes of the chosen type are placed strictly between them:
 *  - spacing "every": at tA + k * step (k = 1, 2, ...) while the time is before tB;
 *  - spacing "count": N notes at tA + k * (tB - tA) / (N + 1), k = 1..N (exact rationals);
 * each at x = xA + (xB - xA) * curve(p) with p = (t - tA) / (tB - tA) measured in beats (the shape follows the
 * musical grid; a Linear curve gives a straight run). Properties come from the start note (side, fake, speed,
 * height offset, width, opacity, appear time, hitsound, colours, judge width) or from the user's "New note
 * defaults". "Include the end notes" turns the two end notes into the fill's type too (Hold ends get one fill step
 * as their length), so the whole run is uniform. Generated holds last one fill step (never past the end note).
 * The new notes are selected; one transaction (the sheet runs it in a gesture for the live ghosts).
 * `fillExpression` is the same X in the expression language (note fields: `t` is the note's beat), e.g.
 * "= lerp(-200, 300, ease(6, (t - 4) / 4))": typed into the X field of the filled notes it gives the same values.
 */

import type { Note, NoteKind, NoteTemplate } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { noteCommands } from '../commands/kit';
import type { NoteDraft } from '../commands/types';
import { Expr, formatEaseCall } from '../expr/index';
import { numText, toolReason, TOOLS_MSG } from './common';
import { rampCurveY } from './ramp';
import type { FillParams, ToolReport } from './types';

/** Most notes one fill may create. */
export const FILL_MAX_NOTES = 4096;

type NoteOverrides = NonNullable<NoteDraft['overrides']>;

/** Note fields the start note lends to the fill. */
function overridesFrom(n: Note): NoteOverrides {
  return {
    above: n.above,
    fake: n.fake,
    speed: n.speed,
    yOffset: n.yOffset,
    size: n.size,
    alpha: n.alpha,
    visibleTime: n.visibleTime,
    hitsound: n.hitsound,
    tint: n.tint,
    tintHitEffects: n.tintHitEffects,
    judgeArea: n.judgeArea,
  };
}

function overridesFromTemplate(t: NoteTemplate): NoteOverrides {
  return {
    above: t.above,
    fake: t.fake,
    speed: t.speed,
    yOffset: t.yOffset,
    size: t.size,
    alpha: t.alpha,
    visibleTime: t.visibleTime,
  };
}

/** Beats strictly between a and b for a spacing (fails when there are too many). */
export function fillBeats(
  tx: TxBuilder,
  a: Beat,
  b: Beat,
  spacing: FillParams['spacing'],
): { beats: Beat[]; step: Beat } {
  const span = Beats.sub(b, a, tx.rounding);
  const out: Beat[] = [];
  if ('every' in spacing) {
    const step = spacing.every;
    if (!Beats.lt(Beats.ZERO, step)) tx.fail(toolReason(TOOLS_MSG.fillBadSpacing));
    for (let t = Beats.add(a, step, tx.rounding); Beats.lt(t, b); t = Beats.add(t, step, tx.rounding)) {
      out.push(t);
      if (out.length > FILL_MAX_NOTES) tx.fail(toolReason(TOOLS_MSG.fillTooMany, { max: FILL_MAX_NOTES }));
    }
    return { beats: out, step };
  }
  const n = spacing.count;
  if (!Number.isSafeInteger(n) || n < 1) tx.fail(toolReason(TOOLS_MSG.fillBadSpacing));
  if (n > FILL_MAX_NOTES) tx.fail(toolReason(TOOLS_MSG.fillTooMany, { max: FILL_MAX_NOTES }));
  for (let k = 1; k <= n; k++) out.push(Beats.add(a, Beats.mulRat(span, k, n + 1, tx.rounding), tx.rounding));
  return { beats: out, step: Beats.mulRat(span, 1, n + 1, tx.rounding) };
}

/** X of a filled note at beat t (curve over beat progress). */
export function fillX(a: Note, b: Note, t: Beat, curve: FillParams['xCurve']): number {
  const ta = Beats.toF64(a.t);
  const tb = Beats.toF64(b.t);
  const p = (Beats.toF64(t) - ta) / (tb - ta);
  const y = rampCurveY(curve, p);
  const x = y === 1 ? b.x : a.x + (b.x - a.x) * y;
  return x === 0 ? 0 : x;
}

/**
 * The X of the filled notes as a note-field expression (`t` = the note's beat), with the earlier note first. It
 * computes exactly what `fillX` does (the language's `lerp` returns the end value at progress 1 too).
 */
export function fillExpression(
  n1: Pick<Note, 't' | 'x'>,
  n2: Pick<Note, 't' | 'x'>,
  curve: FillParams['xCurve'],
): string {
  const [a, b] = Beats.cmp(n1.t, n2.t) <= 0 ? [n1, n2] : [n2, n1];
  const ta = Beats.toF64(a.t);
  const span = Beats.toF64(b.t) - ta;
  const from = ta === 0 ? 't' : ta < 0 ? `(t + ${numText(-ta)})` : `(t - ${numText(ta)})`;
  const progress = span === 1 ? from.replace(/^\((.*)\)$/, '$1') : `${from} / ${numText(span)}`;
  return Expr.format('=', `lerp(${numText(a.x)}, ${numText(b.x)}, ${formatEaseCall(curve, progress)})`);
}

export function fillBetween(tx: TxBuilder, p: FillParams): ToolReport {
  const n1 = tx.doc.notes.get(p.from);
  const n2 = tx.doc.notes.get(p.to);
  if (n1 === undefined || n2 === undefined || p.from === p.to) tx.fail(toolReason(TOOLS_MSG.fillNeedTwo));
  if (n1.line !== n2.line) tx.fail(toolReason(TOOLS_MSG.fillSameLine));
  const c = Beats.cmp(n1.t, n2.t);
  if (c === 0) tx.fail(toolReason(TOOLS_MSG.fillSameTime));
  const [a, b] = c < 0 ? [n1, n2] : [n2, n1];
  const { beats, step } = fillBeats(tx, a.t, b.t, p.spacing);
  if (beats.length === 0) tx.fail(toolReason(TOOLS_MSG.fillNone));
  let overrides: NoteOverrides;
  let template: NoteTemplate;
  if (p.propsFrom === 'template') {
    if (p.template === undefined) tx.fail(toolReason(TOOLS_MSG.fillTemplate));
    template = p.template;
    overrides = overridesFromTemplate(p.template);
  } else {
    overrides = overridesFrom(a);
    template = {
      above: a.above,
      fake: a.fake,
      speed: a.speed,
      yOffset: a.yOffset,
      size: a.size,
      alpha: a.alpha,
      visibleTime: a.visibleTime,
    };
  }
  const kind: NoteKind = p.kind;
  const drafts: NoteDraft[] = beats.map((t) => ({
    line: a.line,
    kind,
    t,
    ...(kind === 'hold' ? { end: Beats.min(Beats.add(t, step, tx.rounding), b.t) } : {}),
    x: fillX(a, b, t, p.xCurve),
    overrides,
  }));
  const ids = noteCommands.addNotes(tx, { drafts, template });
  let ends = 0;
  if (p.includeEnds) {
    const convert = [a, b].filter((n) => n.kind !== kind).map((n) => n.id);
    if (convert.length > 0) {
      noteCommands.convertNotes(tx, { ids: convert, kind, holdLen: step });
      ends = convert.length;
    }
    if (p.propsFrom === 'template') {
      noteCommands.patchNotes(tx, { ids: [a.id, b.id], patch: overridesFromTemplate(template) });
    }
  }
  const selected = p.includeEnds ? [a.id, ...ids, b.id] : ids;
  tx.setSelection({ notes: selected, activeLine: a.line });
  tx.setFocus({ beat: a.t, line: a.line });
  return { changed: ids.length + ends };
}
